/**
 * Phase 1.7 — Gardens module index, v2.
 *
 * Cards are interactive and navigate to /gardens/:id; the view
 * toggle (grid / list) actually re-renders.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Flower2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import { EditEntityDialog } from '@/components/v2/dialogs/EditEntityDialog';
import { DeleteEntityDialog } from '@/components/v2/dialogs/DeleteEntityDialog';
import { EntityImage } from '@/components/EntityImage';

type Status = 'planning' | 'active' | 'fallow' | 'retired';

interface GardenRow {
  id: string;
  ranch_id: string;
  name: string;
  status?: Status | null;
  area_sqft?: number | null;
  irrigation_type?: string | null;
  soil_type?: string | null;
  season_year?: number | null;
  notes?: string | null;
}

const STATUS_ORDER: Status[] = ['active', 'planning', 'fallow', 'retired'];
const STATUS_LABEL: Record<Status, string> = {
  active: 'Active',
  planning: 'Planning',
  fallow: 'Fallow',
  retired: 'Retired',
};
const STATUS_TONE: Record<Status, string> = {
  active: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  planning: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  fallow: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  retired: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
};

const VIEW_KEY = 'gardens';

const FIELDS = [
  { name: 'name', label: 'Name', required: true, placeholder: 'Kitchen garden' },
  {
    name: 'status',
    label: 'Status',
    type: 'select' as const,
    half: true,
    defaultValue: 'active',
    options: STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  },
  { name: 'area_sqft', label: 'Area (sq ft)', type: 'number' as const, half: true, placeholder: '800' },
  { name: 'irrigation_type', label: 'Irrigation', half: true, placeholder: 'Drip' },
  { name: 'soil_type', label: 'Soil', half: true, placeholder: 'Sandy loam' },
  { name: 'season_year', label: 'Season year', type: 'number' as const, placeholder: '2026' },
  { name: 'notes', label: 'Notes', type: 'textarea' as const },
];

const transformGarden = (v: Record<string, string>): Record<string, unknown> => ({
  ...(v.name !== undefined ? { name: v.name } : {}),
  ...(v.status !== undefined ? { status: v.status || 'active' } : {}),
  ...(v.area_sqft !== undefined ? { area_sqft: v.area_sqft ? Number(v.area_sqft) : null } : {}),
  ...(v.irrigation_type !== undefined ? { irrigation_type: v.irrigation_type || null } : {}),
  ...(v.soil_type !== undefined ? { soil_type: v.soil_type || null } : {}),
  ...(v.season_year !== undefined ? { season_year: v.season_year ? Number(v.season_year) : null } : {}),
  ...(v.notes !== undefined ? { notes: v.notes || null } : {}),
});

function statusOf(g: GardenRow): Status {
  return g.status ?? 'active';
}

export function GardensPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [items, setItems] = useState<GardenRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<GardenRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GardenRow | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Gardens',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add garden
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: GardenRow[] }>('/api/gardens');
        if (!cancelled) setItems(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load gardens');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const filtered = useMemo<GardenRow[]>(() => {
    const q = search.trim().toLowerCase();
    return items.filter((g) => {
      if (q && !g.name.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'all' && statusOf(g) !== statusFilter) return false;
      return true;
    });
  }, [items, search, statusFilter]);

  const grouped = useMemo<Record<Status, GardenRow[]>>(() => {
    const out = STATUS_ORDER.reduce<Record<Status, GardenRow[]>>(
      (acc, s) => ({ ...acc, [s]: [] }),
      {} as Record<Status, GardenRow[]>,
    );
    for (const g of filtered) out[statusOf(g)].push(g);
    return out;
  }, [filtered]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setStatusFilter('all'); };
  const refresh = (): void => setRefreshTick((t) => t + 1);
  const stop = (e: React.MouseEvent): void => e.stopPropagation();

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Garden"
        endpoint="/api/gardens"
        entityType="garden"
        fields={FIELDS}
        transform={transformGarden}
        onAdded={refresh}
      />
      {editTarget ? (
        <EditEntityDialog
          open={Boolean(editTarget)}
          onOpenChange={(o) => { if (!o) setEditTarget(null); }}
          title={`Edit ${editTarget.name}`}
          endpoint="/api/gardens"
          entityId={editTarget.id}
          fields={FIELDS}
          initialValues={{
            name: editTarget.name,
            status: statusOf(editTarget),
            area_sqft: editTarget.area_sqft != null ? String(editTarget.area_sqft) : '',
            irrigation_type: editTarget.irrigation_type ?? '',
            soil_type: editTarget.soil_type ?? '',
            season_year: editTarget.season_year != null ? String(editTarget.season_year) : '',
            notes: editTarget.notes ?? '',
          }}
          transform={transformGarden}
          onSaved={refresh}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteEntityDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
          endpoint="/api/gardens"
          entityId={deleteTarget.id}
          entityName={deleteTarget.name}
          onDeleted={refresh}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard label="Gardens" icon={Flower2} primary={{ label: 'Tracked', value: items.length }} secondary={{ label: 'Active', value: items.filter((g) => statusOf(g) === 'active').length }} />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: items.length }} />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search gardens' }}
        filters={[
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | Status)}
            options={[{ value: 'all', label: 'All statuses' }, ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Flower2}
          title={hasActiveFilters ? 'No matches' : 'No gardens yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Add a garden to track plantings, harvests, and bed history.'}
          action={hasActiveFilters ? <Button onClick={clear} variant="outline">Clear filters</Button> : <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" />Add garden</Button>}
        />
      ) : (
        STATUS_ORDER.filter((s) => grouped[s].length > 0).map((status) => (
          <SectionHeader key={status} title={STATUS_LABEL[status]} count={grouped[status].length} collapsible>
            {view === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[status].map((g) => (
                  <Card
                    key={g.id}
                    interactive
                    onClick={() => navigate(`/gardens/${g.id}`)}
                    thumbnail={<EntityImage entityType="garden" entityId={g.id} alt={g.name} />}
                    title={g.name}
                    subtitle={g.area_sqft ? `${g.area_sqft.toLocaleString()} sq ft` : 'Garden'}
                    badge={
                      <span className={`rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(g)]}`}>
                        {STATUS_LABEL[statusOf(g)]}
                      </span>
                    }
                    footer={
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(g); }} aria-label={`Edit ${g.name}`} className="h-7 w-7">
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(g); }} aria-label={`Archive ${g.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                    <tr>
                      <Th>Name</Th>
                      <Th>Area</Th>
                      <Th>Irrigation</Th>
                      <Th>Status</Th>
                      <Th className="text-right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[status].map((g) => (
                      <tr
                        key={g.id}
                        className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                        onClick={() => navigate(`/gardens/${g.id}`)}
                      >
                        <Td className="font-medium">{g.name}</Td>
                        <Td className="font-mono text-xs">{g.area_sqft ? `${g.area_sqft.toLocaleString()} sq ft` : '\u2014'}</Td>
                        <Td className="text-muted-foreground">{g.irrigation_type || '\u2014'}</Td>
                        <Td>
                          <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${STATUS_TONE[statusOf(g)]}`}>
                            {STATUS_LABEL[statusOf(g)]}
                          </span>
                        </Td>
                        <Td className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setEditTarget(g); }} aria-label={`Edit ${g.name}`} className="h-7 w-7">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={(e) => { stop(e); setDeleteTarget(g); }} aria-label={`Archive ${g.name}`} className="h-7 w-7 text-destructive hover:text-destructive">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground ${className ?? ''}`}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
